import TimePanelHeaderButtonWell from './TimePanelHeaderButtonWell';
import { updateCurrentView } from '../actions/timePanelStoreActions';
import {
    getCurrentPanelViewType,
    getStackLength,
    getCurrentPanelView,
} from 'owa-time-panel-view-stack';
import { observer } from 'mobx-react-lite';
import { ActionButton } from 'office-ui-fabric-react/lib/Button';
import { Pivot, PivotItem, PivotLinkSize, IPivotItemProps } from 'office-ui-fabric-react/lib/Pivot';
import { ControlIcons } from 'owa-control-icons';
import loc from 'owa-localize';
import * as React from 'react';
import {
    backButtonText,
    backButtonTitle,
    calendarPivotText,
    tasksPivotText,
} from './TimePanelHeaderBar.locstring.json';
import { Droppable, DragData } from 'owa-dnd';
import {
    getCalendarTabDropState,
    shouldShowCalendarTabHoverHint,
    setLastEnterCalendarTab,
    getLastDragOverHeader,
    resetCalendarTabDropViewState,
} from 'owa-time-panel-dnd';
import { isFeatureEnabled } from 'owa-feature-flags';
import { popView } from 'owa-time-panel-bootstrap';
import { closeComposeFormInTimePanel } from 'owa-time-panel-compose';
import { now, addMilliseconds, compare } from 'owa-datetime';

const styles = require('./TimePanelHeaderBar.scss');
const classNames = require('classnames/bind').bind(styles);
const HOVER_WAIT_TIME = 1000;

export default observer(function TimePanelHeaderBar() {
    return (
        <div className={styles.container}>
            <div className={styles.headerControl}>
                {getStackLength() > 1 ? (
                    <ActionButton
                        className={styles.backButton}
                        styles={{
                            icon: styles.backButtonIcon,
                        }}
                        text={loc(backButtonText)}
                        title={loc(backButtonTitle)}
                        onClick={backButtonClicked}
                        iconProps={{ iconName: ControlIcons.Back }}
                    />
                ) : (
                    <Pivot
                        headersOnly={true}
                        onLinkClick={selectPivot}
                        linkSize={PivotLinkSize.normal}
                        selectedKey={getCurrentPanelViewType()}
                        styles={{ linkIsSelected: styles.pivotLinkSelected }}>
                        {!isFeatureEnabled('cal-time-panel-dragEscalateMeeting') ? (
                            <PivotItem itemKey={'Calendar'} headerText={loc(calendarPivotText)} />
                        ) : (
                            <PivotItem
                                itemKey={'Calendar'}
                                headerText={loc(calendarPivotText)}
                                onRenderItemLink={renderDroppablePivot}
                                className={pivotStyles()}
                            />
                        )}
                        <PivotItem itemKey={'Tasks'} headerText={loc(tasksPivotText)} />
                    </Pivot>
                )}
            </div>
            <TimePanelHeaderButtonWell />
        </div>
    );
});

function backButtonClicked(): void {
    if (getCurrentPanelView() === 'ComposeForm') {
        // If we have compose form opened, then we call closeComposeFormInTimePanel so that we show confirmation dialog if required.
        // closeComposeFormInTimePanel will take care of calling popView when it's done
        closeComposeFormInTimePanel();
    } else {
        popView();
    }
}

function selectPivot(item: PivotItem): void {
    switch (item.props.itemKey) {
        case 'Calendar':
            updateCurrentView('Calendar');
            break;
        case 'Tasks':
            updateCurrentView('Tasks');
            break;
        default:
            throw new Error('Not a valid ItemKey: ' + item.props.itemKey);
    }
}

/**
 * We don't allow dropping onto the tab, only hovering to open the Calendar
 */
function doNothing(dragInfo: DragData, pageX: number, pageY: number, target?: HTMLElement) {
    return;
}

function startHoverCalendarTab(dragInfo: DragData): void {
    setLastEnterCalendarTab(now());
}

function openCalendarAfterHover(): void {
    if (compare(addMilliseconds(getLastDragOverHeader(), HOVER_WAIT_TIME), now()) <= 0) {
        resetCalendarTabDropViewState();
        updateCurrentView('Calendar');
    }
}

function pivotStyles(): string {
    return classNames(
        { dropContainer: shouldShowCalendarTabHoverHint() },
        { dropHoverTreatment: getCalendarTabDropState().isDragOver }
    );
}

function renderDroppablePivot(
    link: IPivotItemProps,
    defaultRenderer: (link: IPivotItemProps) => JSX.Element
): JSX.Element {
    return getCurrentPanelViewType() !== 'Calendar' ? (
        <Droppable
            dropViewState={getCalendarTabDropState()}
            onDrop={doNothing}
            onDragEnter={startHoverCalendarTab}
            onDragOver={openCalendarAfterHover}>
            <div className={pivotStyles()}>{defaultRenderer(link)}</div>
        </Droppable>
    ) : (
        defaultRenderer(link)
    );
}
